import React, { useEffect, useRef, useState } from "react";
import { connect } from "dva";
import {
  Form,
  Steps,
  Button,
  Row,
  Col,
  Card,
  Space,
  Collapse,
  Input,
} from "antd";
import { StoreState } from "@src/interfaces";
import Loading from "@src/components/Loading";
import {
  useTicketForm,
  CLONE_TICKET_FORM_PAGE_ID,
} from "./hooks/useTicketForm";
import {
  EventSection,
  TicketsSection,
  TicketMetaSection,
  EventMetaSection,
  ReviewSection,
  LayoutSection,
} from "./components";
import { helper } from "@src/controls/controlHelper";

const { Step } = Steps;

interface CloneTicketFormCtrlProps {
  query?: any;
}

const CloneTicketFormCtrl: React.FC<CloneTicketFormCtrlProps> = (props) => {
  const [form] = Form.useForm();
  const [current, setCurrent] = useState<number>(0);
  // Guard: ensure edit prefill runs only once per session to avoid overwriting user edits on step changes
  const prefilledRef = useRef<boolean>(false);
  const {
    PAGE_ID,
    initialValues,
    loading,
    defaultTicket,
    onFinish,
    // loadMetaDataForEdit,
    loadEventTicketInfo,
  } = useTicketForm(props?.query, {
    cloneFromExisting: true,
    pageId: CLONE_TICKET_FORM_PAGE_ID,
  });

  // Extract edit identifiers to stable variables for effect dependencies
  const query = props?.query;
  const editId = query?.id;
  const editTicketId = query?.ticketId;
  const editEventId = query?.eventId;

  useEffect(() => {
    const hasEditIds = Boolean(editId || editTicketId || editEventId);

    if (!hasEditIds) return;
    if (prefilledRef.current) return;
    const fetchForEdit = async () => {
      // Ưu tiên API mới: find_event_ticket_info
      const { envent, ticket, eventMeta, ticketMeta } =
        await loadEventTicketInfo();

      const cur = form.getFieldsValue(true) || {};

      const baseTickets =
        Array.isArray(ticket) && ticket.length > 0
          ? ticket
          : Array.isArray(cur.ticket) && cur.ticket.length > 0
          ? cur.ticket
          : [{ ...defaultTicket }];

      const nextValue: any = {
        ...cur,
        envent: {
          ...(cur.envent || {}),
          ...(envent || {}),
          ...(Array.isArray(eventMeta) && eventMeta.length
            ? { metaProperties: eventMeta }
            : {}),
        },
        ticket: baseTickets,
      };

      if (Array.isArray(ticketMeta) && ticketMeta.length) {
        const { data: withIds } = ensureMetaIds(ticketMeta);
        nextValue.metaData = withIds;
        // lưu snapshot để tránh đồng bộ lặp khi onValuesChange chạy
        prevGlobalMetaRef.current = withIds;
      }

      form.setFieldsValue(nextValue);
      prefilledRef.current = true;
    };
    fetchForEdit();
  }, [
    form,
    loadEventTicketInfo,
    defaultTicket,
    editId,
    editTicketId,
    editEventId,
  ]);

  // Auto-select default layout even if user doesn't open the layout step
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        const current = form.getFieldValue(["envent", "ticketLayoutId"]);
        if (current != null && current !== undefined && current !== "") return;
        const pageInfo = await helper.getPage(PAGE_ID);
        const rs: any = await helper.callPageApi(
          pageInfo,
          "find_ticketLayout",
          {
            default: true,
          }
        );
        const raw = rs?.data?.data ?? rs?.data ?? rs ?? {};
        let list: any[] = [];
        if (Array.isArray(raw)) list = raw;
        else if (Array.isArray(raw?.data)) list = raw.data;
        else if (Array.isArray(raw?.list)) list = raw.list;
        const def = Array.isArray(list)
          ? list.find((it) => !!it?.default)
          : undefined;
        if (mounted && def && def.id) {
          form.setFieldsValue({ envent: { ticketLayoutId: def.id } });
        }
      } catch {}
    };
    run();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, editEventId, editId, PAGE_ID]);

  const next = async () => {
    try {
      if (current === 0) {
        await form.validateFields([["envent", "nameEvent"]] as any);
        // Nếu người dùng bấm nút chuyển sang bước layout trong EventSection, ta sẽ nhảy thẳng đến bước Layout
        const goLayout = form.getFieldValue(["__goToLayoutStep"]);
        if (goLayout) {
          form.setFieldsValue({ __goToLayoutStep: undefined });
          setCurrent(2);
          return;
        }
      }
      if (current === 1) {
        await form.validateFields([["ticket"]] as any);
      }
      setCurrent(current + 1);
    } catch (e) {}
  };

  const prev = () => setCurrent(current - 1);

  const StepFooter = () => (
    <Space>
      {current > 0 && (
        <Button onClick={prev} disabled={loading}>
          Quay lại
        </Button>
      )}
      {current < 3 && (
        <Button type="primary" onClick={next} loading={loading}>
          Tiếp tục
        </Button>
      )}
      {current === 3 && (
        <Button type="primary" htmlType="submit" loading={loading}>
          Lưu
        </Button>
      )}
    </Space>
  );

  // Tạo id ổn định cho từng metadata chung để tránh nhân bản khi người dùng gõ tên
  const ensureMetaIds = (meta?: any[]) => {
    const arr = Array.isArray(meta) ? meta : [];
    let changed = false;
    const withIds = arr.map((m: any, idx: number) => {
      if (m && !m.__gid) {
        changed = true;
        return { ...m, __gid: `g_${idx}_${Date.now()}` };
      }
      return m;
    });
    return { data: withIds, changed };
  };

  // Đồng bộ metadata chung vào từng vé theo __gid (ưu tiên), fallback theo name; không đụng vào value của vé
  const mergeMetaToTicket = (
    ticketMeta: any[] | undefined,
    globalMeta: any[] | undefined
  ) => {
    const base = Array.isArray(ticketMeta) ? ticketMeta : [];
    const g = Array.isArray(globalMeta) ? globalMeta : [];

    // Lập bảng hiện có theo khóa ưu tiên: __gid, nếu không có thì theo NAME
    const indexOfKey = (arr: any[], key: string) =>
      arr.findIndex(
        (x) =>
          (x?.__gid
            ? `gid:${x.__gid}`
            : `name:${String(x?.name || "").toUpperCase()}`) === key
      );
    const buildKey = (x: any) =>
      x?.__gid
        ? `gid:${x.__gid}`
        : `name:${String(x?.name || "").toUpperCase()}`;

    const result: any[] = [];

    // Giữ các item hiện có nhưng loại trùng key (ưu tiên item đầu)
    const seen = new Set<string>();
    base.forEach((item) => {
      const k = buildKey(item);
      if (!seen.has(k)) {
        seen.add(k);
        result.push(item);
      }
    });

    // Áp metadata chung: cập nhật hoặc thêm mới nếu chưa có
    g.forEach((gm) => {
      const k = buildKey(gm);
      const idx = indexOfKey(result, k);
      if (idx >= 0) {
        // Cập nhật tên/mô tả, giữ nguyên value hiện có
        const cur = result[idx] || {};
        result[idx] = {
          ...cur,
          name: gm?.name,
          description: gm?.description,
          __gid: gm?.__gid,
        };
      } else {
        // Thêm mới, không đặt value
        result.push({
          name: gm?.name,
          description: gm?.description,
          value: "",
          __gid: gm?.__gid,
        });
      }
    });

    return result;
  };

  // Theo dõi thay đổi meta chung để đồng bộ ngay cả khi người dùng gõ ký tự
  const prevGlobalMetaRef = useRef<any[]>([]);
  const buildKey = (x: any) =>
    x?.__gid ? `gid:${x.__gid}` : `name:${String(x?.name || "").toUpperCase()}`;
  const metaEquals = (a?: any[], b?: any[]) => {
    const aa = Array.isArray(a) ? a : [];
    const bb = Array.isArray(b) ? b : [];
    if (aa.length !== bb.length) return false;
    const mapA: Record<string, any> = {};
    aa.forEach((it) => {
      mapA[buildKey(it)] = it;
    });
    for (const it of bb) {
      const k = buildKey(it);
      const x = mapA[k];
      if (!x) return false;
      if (String(x?.name || "") !== String(it?.name || "")) return false;
      if (String(x?.description || "") !== String(it?.description || ""))
        return false;
    }
    return true;
  };

  return (
    <Form
      form={form}
      layout="vertical"
      name={`create_ticket_form_${PAGE_ID}`}
      initialValues={initialValues}
      onFinish={() => onFinish(form.getFieldsValue(true))}
      onValuesChange={(changedValues, allValues) => {
        if (
          changedValues &&
          Object.prototype.hasOwnProperty.call(
            changedValues,
            "__goToLayoutStep"
          ) &&
          changedValues.__goToLayoutStep
        ) {
          form.setFieldsValue({ __goToLayoutStep: undefined });
          setCurrent(2);
          return;
        }
        const currentGlobal = Array.isArray(allValues?.metaData)
          ? allValues.metaData
          : [];
        const { data: globalWithIds, changed } = ensureMetaIds(currentGlobal);
        // Nếu vừa gắn __gid, cập nhật lại form để lưu id
        if (changed) {
          form.setFieldsValue({ metaData: globalWithIds });
        }

        // Nếu meta chung thay đổi (gõ ký tự, sửa tên/mô tả, thêm/xóa), thì đồng bộ xuống vé
        if (!metaEquals(prevGlobalMetaRef.current, globalWithIds)) {
          // Lấy ticket từ form để đảm bảo giá trị được preserve khi step vé chưa render
          const ticketsInForm = form.getFieldValue(["ticket"]);
          const tickets = Array.isArray(ticketsInForm)
            ? ticketsInForm
            : Array.isArray(allValues?.ticket)
            ? allValues.ticket
            : undefined;

          // Nếu chưa có ticket (step vé chưa khởi tạo), không ghi đè giá trị ticket
          if (!Array.isArray(tickets) || tickets.length === 0) {
            prevGlobalMetaRef.current = globalWithIds;
            return;
          }

          // Xóa item đã bị remove ở meta chung
          const globalKeys = new Set<string>(globalWithIds.map(buildKey));
          const syncedTickets = tickets.map((t: any) => {
            const base = Array.isArray(t?.metaData) ? t.metaData : [];
            const filtered = base.filter(
              (it: any) => globalKeys.has(buildKey(it)) || !it?.__gid
            );
            // Merge cập nhật name/description và thêm mới nếu cần
            const meta = mergeMetaToTicket(filtered, globalWithIds);
            return { ...t, metaData: meta };
          });
          form.setFieldsValue({ ticket: syncedTickets });
          prevGlobalMetaRef.current = globalWithIds;
        }
      }}
    >
      {/* Hidden field to register __goToLayoutStep so onValuesChange triggers */}
      <Form.Item name="__goToLayoutStep" noStyle>
        <Input type="hidden" />
      </Form.Item>
      {loading && <Loading />}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Steps
              current={current}
              size="small"
              onChange={(v) => setCurrent(v)}
            >
              <Step title="Sự kiện" />
              <Step title="Vé" />
              <Step title="Thiết kế layout" />
              <Step title="Kiểm tra & Lưu" />
            </Steps>
          </Card>
        </Col>

        <Col span={24}>
          {current === 0 && (
            <Collapse defaultActiveKey={["1"]}>
              <Collapse.Panel header="Thông tin sự kiện" key="1">
                <EventSection
                  form={form}
                  pageId={PAGE_ID}
                  onGoLayoutStep={() => setCurrent(2)}
                />
              </Collapse.Panel>
              <Collapse.Panel header="Meta properties" key="2">
                <EventMetaSection />
              </Collapse.Panel>
            </Collapse>
          )}

          {current === 1 && (
            <Collapse defaultActiveKey={["1"]}>
              <Collapse.Panel header="Vé" key="1">
                <Card
                  type="inner"
                  title="Thông tin vé"
                  style={{ marginBottom: 12 }}
                >
                  <Form.List name={["ticket"]}>
                    {(fields, { add, remove }) => (
                      <TicketsSection
                        fields={fields}
                        add={(v, insertIndex) => {
                          const values = form.getFieldsValue(true) || {};
                          const { data: globalWithIds, changed } =
                            ensureMetaIds(values?.metaData);
                          if (changed) {
                            form.setFieldsValue({ metaData: globalWithIds });
                          }
                          const base = v || { ...defaultTicket };
                          const merged = mergeMetaToTicket(
                            base?.metaData,
                            globalWithIds
                          );
                          add({ ...base, metaData: merged }, insertIndex);
                          // cập nhật snapshot để onValuesChange không cần đồng bộ lại ngay
                          prevGlobalMetaRef.current = globalWithIds;
                        }}
                        remove={remove}
                        form={form}
                        pageId={PAGE_ID}
                      />
                    )}
                  </Form.List>
                </Card>
              </Collapse.Panel>
              <Collapse.Panel
                header="Metadata vé (áp dụng cho tất cả vé)"
                key="2"
              >
                <TicketMetaSection form={form} pageId={PAGE_ID} />
              </Collapse.Panel>
            </Collapse>
          )}

          {current === 2 && (
            <Collapse defaultActiveKey={["1"]}>
              <Collapse.Panel header="Thiết kế layout vé" key="1">
                <LayoutSection
                  form={form}
                  pageId={PAGE_ID}
                  query={props?.query}
                />
              </Collapse.Panel>
            </Collapse>
          )}

          {current === 3 && (
            <Collapse defaultActiveKey={["1"]}>
              <Collapse.Panel header="Kiểm tra thông tin trước khi lưu" key="1">
                <ReviewSection form={form} pageId={PAGE_ID} />
              </Collapse.Panel>
            </Collapse>
          )}
        </Col>

        <Col span={24}>
          <div style={{ position: "sticky", bottom: 0, zIndex: 10 }}>
            <Card>
              <Form.Item shouldUpdate noStyle>
                {() => <StepFooter />}
              </Form.Item>
            </Card>
          </div>
        </Col>
      </Row>
    </Form>
  );
};

const mapStateToProps = ({ auth }: StoreState) => ({
  authUser: auth.authUser,
});

export default connect(mapStateToProps)(CloneTicketFormCtrl);
